import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Users, UserPlus, Trash2, Crown, User, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/use-workspace";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
};

interface Member {
  id: string;
  user_id: string;
  role: string;
  created_at: string;
  full_name: string;
  email: string;
}

export default function WorkspaceMembers() {
  const { activeWorkspace } = useWorkspace();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) setCurrentUserId(user.id);
    };
    init();
  }, []);

  useEffect(() => {
    if (!activeWorkspace?.id) return;
    fetchMembers();
  }, [activeWorkspace?.id]);

  const fetchMembers = async () => {
    if (!activeWorkspace?.id) return;
    setLoading(true);

    const { data, error } = await supabase
      .from("workspace_members")
      .select("id, user_id, role, created_at")
      .eq("workspace_id", activeWorkspace.id);

    if (error) {
      console.error("Error fetching members:", error);
      setLoading(false);
      return;
    }

    // Fetch profiles for each member
    const userIds = (data || []).map((m) => m.user_id);
    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, full_name")
      .in("id", userIds);

    const profileMap = new Map((profiles || []).map((p) => [p.id, p.full_name || ""]));

    const enriched: Member[] = (data || []).map((m) => ({
      ...m,
      full_name: profileMap.get(m.user_id) || "Unknown",
      email: "", // We don't have access to auth.users emails from client
    }));

    setMembers(enriched);
    setIsOwner(enriched.some((m) => m.user_id === currentUserId && m.role === "owner"));
    setLoading(false);
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !activeWorkspace?.id) return;
    setInviting(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const res = await supabase.functions.invoke("workspace-invite", {
        body: { email: inviteEmail.trim(), workspace_id: activeWorkspace.id },
      });

      if (res.error) {
        const errorMsg = res.error.message || "Failed to invite user";
        toast({ title: "Invite failed", description: errorMsg, variant: "destructive" });
      } else if (res.data?.error) {
        toast({ title: "Invite failed", description: res.data.error, variant: "destructive" });
      } else {
        toast({ title: "Member added", description: `${inviteEmail} has been added to the workspace.` });
        setInviteEmail("");
        fetchMembers();
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (memberId: string, memberName: string) => {
    const { error } = await supabase
      .from("workspace_members")
      .delete()
      .eq("id", memberId);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Member removed", description: `${memberName} has been removed from the workspace.` });
      fetchMembers();
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="px-6 py-10 sm:py-16 max-w-3xl mx-auto w-full">
        <motion.div {...fadeUp} className="mb-10">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 mb-4 font-mono text-xs text-muted-foreground"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back
          </Button>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card mb-6">
            <Users className="w-3.5 h-3.5 text-accent" />
            <span className="font-mono text-xs text-muted-foreground">Workspace</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold leading-tight tracking-tight mb-3">
            {activeWorkspace?.name || "Workspace"} — Members
          </h1>
          <p className="text-muted-foreground leading-relaxed max-w-lg">
            Manage who has access to this workspace. Members can view shared deals.
          </p>
        </motion.div>

        <div className="space-y-8">
          {/* Invite Section — only for owners */}
          {isOwner && (
            <motion.div {...fadeUp}>
              <Card>
                <CardHeader>
                  <CardTitle className="font-display text-lg flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-accent" />
                    Invite Member
                  </CardTitle>
                  <CardDescription className="font-mono text-xs">
                    Add an existing Brickshare user to this workspace by email. They'll be added as a Member with view access to shared deals.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Label className="sr-only">Email address</Label>
                      <Input
                        type="email"
                        placeholder="colleague@company.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleInvite()}
                        className="font-mono"
                      />
                    </div>
                    <Button
                      onClick={handleInvite}
                      disabled={!inviteEmail.trim() || inviting}
                      className="gap-1.5 font-display"
                    >
                      {inviting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                      {inviting ? "Inviting…" : "Invite"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Members List */}
          <motion.div {...fadeUp}>
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-lg">
                  Members ({members.length})
                </CardTitle>
                <CardDescription className="font-mono text-xs">
                  All users with access to this workspace.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : members.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No members found.</p>
                ) : (
                  <div className="space-y-1">
                    {members.map((member, i) => (
                      <div key={member.id}>
                        {i > 0 && <Separator className="my-1" />}
                        <div className="flex items-center gap-3 py-3 px-2 rounded-md hover:bg-muted/50 transition-colors">
                          <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                            {member.role === "owner" ? (
                              <Crown className="w-3.5 h-3.5 text-accent" />
                            ) : (
                              <User className="w-3.5 h-3.5 text-muted-foreground" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium truncate">
                                {member.full_name}
                                {member.user_id === currentUserId && (
                                  <span className="text-muted-foreground ml-1">(you)</span>
                                )}
                              </span>
                              <Badge
                                variant={member.role === "owner" ? "default" : "secondary"}
                                className="font-mono text-2xs uppercase"
                              >
                                {member.role}
                              </Badge>
                            </div>
                            <p className="font-mono text-2xs text-muted-foreground">
                              Joined {new Date(member.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          {/* Owner can remove members (but not themselves) */}
                          {isOwner && member.user_id !== currentUserId && member.role !== "owner" && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="shrink-0 text-muted-foreground hover:text-destructive">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Remove member?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {member.full_name} will lose access to this workspace and its shared deals.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleRemove(member.id, member.full_name)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Remove
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
